import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Instagram, Facebook, MessageCircle, Send } from "lucide-react";
import { useLang } from "@/App";
import { apiUrl } from "@/lib/api";

const SECCIONES = [
  ["inicio", ""],
  ["habitaciones", "habitaciones"],
  ["gastronomia", "gastronomia"],
  ["servicios", "servicios"],
  ["queHacer", "que-hacer"],
  ["eventos", "eventos"],
  ["fotos", "fotos"],
  ["contacto", "contacto"],
];

export default function Footer() {
  const { t, lang, L } = useLang();
  const [email, setEmail] = useState("");
  const [estado, setEstado] = useState(null);

  const suscribir = async (e) => {
    e.preventDefault();
    if (!email) return;
    setEstado("enviando");
    try {
      const r = await fetch(apiUrl("/api/newsletter"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, idioma: lang }),
      });
      if (!r.ok) throw new Error();
      setEstado("ok");
      setEmail("");
    } catch {
      setEstado("error");
    }
  };

  const g = t.global.footer;
  const d = t.contacto?.datos || {};

  return (
    <footer className="border-t border-ink bg-ink" data-testid="footer">
      <div className="mx-auto flex max-w-[1360px] flex-col gap-12 px-6 py-16 md:px-10 md:py-20 lg:flex-row lg:justify-between lg:gap-8">
        <div className="lg:max-w-[250px]" data-testid="footer-identidad">
          <p className="font-serif text-2xl text-sand">{g.marca}</p>
          <ul className="mt-6 space-y-3.5 text-sm text-sand/70">
            <li className="flex items-start gap-3">
              <MapPin size={16} strokeWidth={1.6} className="mt-0.5 shrink-0 text-terra" />
              <span>{g.direccion}</span>
            </li>
            <li>
              <a href="https://wa.me/541167931464" target="_blank" rel="noreferrer" className="flex items-center gap-3 transition-colors hover:text-sand" data-testid="footer-wa1">
                <MessageCircle size={16} strokeWidth={1.6} className="shrink-0 text-terra" />
                {d.tel1 || "011 6793-1464"}
              </a>
            </li>
            <li>
              <a href="https://wa.me/541165683169" target="_blank" rel="noreferrer" className="flex items-center gap-3 transition-colors hover:text-sand" data-testid="footer-wa2">
                <MessageCircle size={16} strokeWidth={1.6} className="shrink-0 text-terra" />
                {d.tel2 || "011 6568-3169"}
              </a>
            </li>
            <li>
              <a href={`mailto:${d.reservas || "lacarlareservas@gmail.com"}`} className="flex items-center gap-3 transition-colors hover:text-sand" data-testid="footer-email">
                <Mail size={16} strokeWidth={1.6} className="shrink-0 text-terra" />
                {d.reservas || "lacarlareservas@gmail.com"}
              </a>
            </li>
            <li className="flex items-center gap-4 pt-1">
              <a href={d.ig || "https://www.instagram.com/fincalacarla/"} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-sand/70 transition-colors hover:text-sand" data-testid="footer-instagram">
                <Instagram size={18} strokeWidth={1.6} />
              </a>
              <a href={d.fb || "https://www.facebook.com/profile.php?id=61582360713302"} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-sand/70 transition-colors hover:text-sand" data-testid="footer-facebook">
                <Facebook size={18} strokeWidth={1.6} />
              </a>
            </li>
          </ul>
        </div>

        <div data-testid="footer-secciones">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-terra">{g.seccionesTitulo}</p>
          <ul className="mt-6 space-y-3 text-sm">
            {SECCIONES.map(([k, to]) => (
              <li key={k}>
                <Link to={L(to)} className="text-sand/70 transition-colors hover:text-sand" data-testid={`footer-seccion-${k}`}>
                  {t.nav[k]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-mas-info">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-terra">{g.masTitulo}</p>
          <ul className="mt-6 space-y-3 text-sm">
            {g.mas.map((m) => (
              <li key={m.to}>
                <Link to={L(m.to)} className="text-sand/70 transition-colors hover:text-sand" data-testid={`footer-mas-${m.to}`}>
                  {m.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-newsletter">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-terra">{g.newsletterTitulo}</p>
          <p className="mt-6 text-sm text-sand/70">{g.newsletterTexto}</p>
          <form onSubmit={suscribir} className="mt-5">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={g.newsletterPlaceholder}
              className="w-full border border-sand/25 bg-sand/10 px-4 py-3 text-sm text-sand outline-none transition-colors placeholder:text-sand/40 focus:border-terra"
              data-testid="footer-newsletter-input"
            />
            <button
              type="submit"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 bg-terra px-5 py-3 text-[11px] font-bold tracking-[0.2em] text-sand transition-colors hover:bg-terra-dark"
              data-testid="footer-newsletter-btn"
            >
              <Send size={13} /> {g.newsletterBtn}
            </button>
            {estado === "ok" && <p className="mt-3 text-xs text-sand/80" data-testid="footer-newsletter-ok">{lang === 'es' ? '¡Gracias por suscribirte!' : lang === 'fr' ? 'Merci de votre inscription !' : lang === 'it' ? 'Grazie per l\'iscrizione!' : 'Thank you for subscribing!'}</p>}
          </form>
        </div>
      </div>

      <div className="border-t border-sand/10 py-6 text-center text-xs text-sand/45" data-testid="footer-copyright">
        © {new Date().getFullYear()} · {g.copyright}
      </div>
    </footer>
  );
}